import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, Globe, Receipt, Users, Wallet, CreditCard, 
  TrendingDown, PiggyBank, Package, Bell, Table as TableIcon,
  Smartphone, RefreshCw, HelpCircle, Eye, Clock, Languages,
  User, DollarSign, Calendar, History, MessageSquare, Bike,
  Monitor, Columns, UtensilsCrossed, Printer, Percent, 
  Settings, ToggleLeft, RotateCw, ListOrdered, X, ArrowLeft,
  Plus, Minus, Trash2, Gift, Search, Sun, Download, GripVertical, Layers, ShoppingCart, Phone, MapPin, TrendingUp, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';
import OffersManager from '@/components/POS/OffersManager';
import CustomerHistoryLookup from '@/components/POS/CustomerHistoryLookup';
import { hasCapability } from '@/lib/capabilities';


const POSDashboard = () => {
  const [user, setUser] = useState(null);
  const [showModule, setShowModule] = useState(null);
  const [showSmsVerifyModal, setShowSmsVerifyModal] = useState(false);
  const [smsVerifyNumber, setSmsVerifyNumber] = useState('');
  const [showVerifyPhoneModal, setShowVerifyPhoneModal] = useState(false);
  const [manualVerifyPhone, setManualVerifyPhone] = useState('');
  const [manualVerifyLoading, setManualVerifyLoading] = useState(false);
  const [showOrderNotificationsModal, setShowOrderNotificationsModal] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(true);
  const [showStoreLocationModal, setShowStoreLocationModal] = useState(false);
  const [storeLat, setStoreLat] = useState('');
  const [storeLng, setStoreLng] = useState('');
  const [storeLocationLoading, setStoreLocationLoading] = useState(false);
  const [acceptingOrders, setAcceptingOrders] = useState(true);
  const [showStopOpsConfirm, setShowStopOpsConfirm] = useState(false);
  const [stopOpsLoading, setStopOpsLoading] = useState(false);
  const [showStoreHoursModal, setShowStoreHoursModal] = useState(false);
  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const [closePromptLoading, setClosePromptLoading] = useState(false);
  const [scheduledLeaves, setScheduledLeaves] = useState([]);
  const [newLeaveDate, setNewLeaveDate] = useState('');
  const [newLeaveReason, setNewLeaveReason] = useState('');
  const [storeHours, setStoreHours] = useState({
    monday: { open: '10:00', close: '23:00', closed: false },
    tuesday: { open: '10:00', close: '23:00', closed: false },
    wednesday: { open: '10:00', close: '23:00', closed: false },
    thursday: { open: '10:00', close: '23:00', closed: false },
    friday: { open: '10:00', close: '23:00', closed: false },
    saturday: { open: '10:00', close: '23:00', closed: false },
    sunday: { open: '10:00', close: '23:00', closed: false },
  });
  const [storeHoursLoading, setStoreHoursLoading] = useState(false);
  const [showOffersManager, setShowOffersManager] = useState(false);
  const [showCustomerLookup, setShowCustomerLookup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // For button reordering
  const [draggedModule, setDraggedModule] = useState(null); // For drag & drop
  const navigate = useNavigate();
  
  // Data states
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [riders, setRiders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [cashFlow, setCashFlow] = useState({ inflow: 0, outflow: 0, balance: 0 });
  
  // Day settings state
  const [daySettings, setDaySettings] = useState({
    day_start_time: '09:00',
    day_end_time: '23:00',
    is_day_started: false
  });
  const [showDayStartPopup, setShowDayStartPopup] = useState(false);
  
  // Tax configuration
  const [taxConfig, setTaxConfig] = useState({
    enabled: true,
    taxes: [
      { id: 1, name: 'CGST', percent: 2.5, enabled: true },
      { id: 2, name: 'SGST', percent: 2.5, enabled: true }
    ],
    serviceCharge: {
      enabled: true,
      percent: 5
    }
  });
  
  // NEW COMPREHENSIVE DISCOUNT SYSTEM
  // Each discount can have: mode restrictions, category discounts, item discounts, and fallback bill discount
  const [discountConfig, setDiscountConfig] = useState(() => {
    const saved = localStorage.getItem('pos_discounts_v2');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      discounts: [
        { 
          id: 1, 
          name: 'Dine-In Special', 
          type: 'percent',
          enabled: true,
          // Order mode restrictions
          allowedModes: ['DINE_IN'],
          // Category-wise discounts (multiple categories, custom %)
          categoryDiscounts: { 'Pizza': 15, 'Burger': 10 },
          // Item-level discounts (optional, overrides category)
          itemDiscounts: {},
          // Fallback bill-level discount (only if no item/category discount)
          billDiscount: 0
        },
        { 
          id: 2, 
          name: 'Takeaway Offer', 
          type: 'percent',
          enabled: true,
          allowedModes: ['TAKEAWAY'],
          categoryDiscounts: { 'Pizza': 20 },
          itemDiscounts: {},
          billDiscount: 5
        },
        { 
          id: 3, 
          name: 'Staff Discount', 
          type: 'percent',
          enabled: true,
          allowedModes: ['DINE_IN', 'TAKEAWAY', 'DELIVERY'],
          categoryDiscounts: {},
          itemDiscounts: {},
          billDiscount: 20
        }
      ]
    };
  });
  
  // Save discount config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pos_discounts_v2', JSON.stringify(discountConfig));
  }, [discountConfig]);
  
  // New discount form state for comprehensive discount creation
  const [discountForm, setDiscountForm] = useState({
    name: '',
    type: 'percent',
    allowedModes: ['DINE_IN'], // Default to Dine-In only
    categoryDiscounts: {}, // { 'Pizza': 30, 'Burger': 10 }
    itemDiscounts: {}, // { 'item-id-1': { name: 'Veg Pizza', percent: 35 } }
    billDiscount: 0
  });
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  
  // Available order modes for discount configuration
  const ORDER_MODES = [
    { id: 'DINE_IN', label: 'Dine-In', icon: '🍽️' },
    { id: 'TAKEAWAY', label: 'Takeaway', icon: '🥡' },
    { id: 'DELIVERY', label: 'Delivery', icon: '🛵' },
    { id: 'SWIGGY', label: 'Swiggy', icon: '🟠' },
    { id: 'ZOMATO', label: 'Zomato', icon: '🔴' }
  ];
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({ category: '', amount: '', description: '' });
  
  // Cash drawer
  const [cashDrawer, setCashDrawer] = useState({ opening: 0, current: 0, withdrawals: 0, topups: 0 });
  const [cashAction, setCashAction] = useState({ type: '', amount: '', reason: '' });
  
  // Customer edit/add
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({ name: '', phone: '', address: '' });
  const [showCustomerForm, setShowCustomerForm] = useState(false);

  // Menu item form for creating/editing

  const [menuItemForm, setMenuItemForm] = useState({ 

    name: '', 

    category_id: '', 

    variants: [{ name: 'Regular', price: '' }],

    description: '',

    short_code: '',

    active: true,

    is_veg: true,

    tax_class: 'standard',

    is_combo: false,

    combo_items: []

  });

  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [showMenuItemForm, setShowMenuItemForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const loadDashboardData = async (storeId) => {
    try {
      // Load orders
      // Raised from 100 — with the full real order history now merged in
      // (2,144+ orders), a low cap meant the Customers tile and dashboard
      // couldn't see older customers at all. This comfortably covers
      // current volume with room to grow.
      const ordersRes = await apiClient.get(`/orders?store_id=${storeId}&limit=5000`);
      setOrders(ordersRes.data || []);
      
      // Calculate cash flow from orders
      const todayOrders = (ordersRes.data || []).filter(o => {
        const orderDate = new Date(o.created_at);
        const today = new Date();
        return orderDate.toDateString() === today.toDateString();
      });
      
      const inflow = todayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      setCashFlow({ inflow, outflow: 0, balance: inflow });
      
      // Load riders
      try {
        const ridersRes = await apiClient.get(`/auth/riders?store_id=${storeId}`);
        setRiders(ridersRes.data || []);
      } catch (e) {
        setRiders([]);
      }
      



      // Load categories from the real catalog system first, since menu

      // items need the category name resolved from category_id

      let catNameById = {};

      try {

        const catRes = await apiClient.get(`/catalog/${storeId}/categories`);

        const cats = catRes.data || [];

        setCategories(cats);

        cats.forEach(c => { catNameById[c.id] = c.name; });

      } catch (e) {

        setCategories([]);

      }



      // Load menu items from the real catalog (catalog_products) - the

      // single source of truth also used by Assisted Order and the main

      // POS screen, not the old menu_items collection.

      try {


        const prodRes = await apiClient.get(`/catalog/${storeId}/products`);

        // Add flat category/price fields for backward compatibility with

        // the rest of this screen's existing display code, alongside the

        // real category_id/variants fields the save function actually uses.

        const flatMenuItems = (prodRes.data || []).map(p => {

          const defaultVariant = (p.variants || []).find(v => v.is_default) || (p.variants || [])[0] || { price: 0 };

          return {

            ...p,

            category: catNameById[p.category_id] || 'Other',

            price: (defaultVariant.price || 0) / 100

          };

        });

        setMenuItems(flatMenuItems);


      } catch (e) {
        setMenuItems([]);
      }

      // Load customers from orders
      const customerMap = new Map();
      (ordersRes.data || []).forEach(order => {
        if (order.customer?.phone) {
          customerMap.set(order.customer.phone, {
            ...order.customer,
            lastOrder: order.created_at,
            totalOrders: (customerMap.get(order.customer.phone)?.totalOrders || 0) + 1
          });
        }
      });
      setCustomers(Array.from(customerMap.values()));
      
      // Load day settings
      try {
        const daySettingsRes = await apiClient.get(`/day-operations/settings/${storeId}`);
        setDaySettings(daySettingsRes.data || {
          day_start_time: '09:00',
          day_end_time: '23:00',
          is_day_started: false
        });
        
        // Show popup if day not started and first login
        if (!daySettingsRes.data?.is_day_started && !localStorage.getItem('day_popup_shown_today')) {
          setShowDayStartPopup(true);
          localStorage.setItem('day_popup_shown_today', new Date().toDateString());
        }
      } catch (e) {
        console.error('Error loading day settings:', e);
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    // Use functional update pattern
    const initializeData = async () => {
      setUser(userData);
      await loadDashboardData(userData.store_id);
      try {
        const storeRes = await apiClient.get(`/stores/${userData.store_id}`);
        setAcceptingOrders(storeRes.data?.config?.accepting_orders !== false);
        const savedTiming = storeRes.data?.config?.store_timing;
        if (savedTiming && Object.keys(savedTiming).length > 0) {
          setStoreHours(prev => ({ ...prev, ...savedTiming }));
        }
        setScheduledLeaves(storeRes.data?.config?.scheduled_leaves || []);
      } catch (e) {
        console.error('Error loading store operations status:', e);
      }
    };
    initializeData();
  }, [navigate]);

  // Checks whether the day needs to auto-close (scheduled time passed)
  // or needs staff to answer the "orders still in progress, continue?"
  // prompt. Piggybacks on regular polling rather than needing a
  // separate background job - genuinely reliable since this screen is
  // already open and active during business hours.
  useEffect(() => {
    if (!user?.store_id) return;
    const checkDayStatus = async () => {
      try {
        const res = await apiClient.get(`/day-operations/status/${user.store_id}`);
        setShowClosePrompt(res.data?.show_close_prompt || false);
        if (res.data?.auto_closed_notice) {
          toast.info(res.data.auto_closed_notice, { duration: 10000 });
          setDaySettings(prev => ({ ...prev, is_day_started: false }));
        }
      } catch (e) {
        console.error('Error checking day status:', e);
      }
    };
    checkDayStatus();
    const interval = setInterval(checkDayStatus, 60000);
    return () => clearInterval(interval);
  }, [user?.store_id]);

  const handleClosePromptResponse = async (response) => {
    setClosePromptLoading(true);
    try {
      const res = await apiClient.post('/day-operations/close-prompt-response', {
        store_id: user?.store_id,
        response
      });
      toast.info(res.data?.message || 'Response recorded');
      setShowClosePrompt(false);
      if (response === 'no') {
        setDaySettings(prev => ({ ...prev, is_day_started: false }));
      }
    } catch (e) {
      toast.error('Failed to record response');
    } finally {
      setClosePromptLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const newExpense = {
      id: Date.now(),
      category: expenseForm.category,
      amount: parseFloat(expenseForm.amount),
      description: expenseForm.description,
      date: new Date().toISOString(),
      addedBy: user?.name
    };
    
    setExpenses([...expenses, newExpense]);
    setCashFlow(prev => ({ 
      ...prev, 
      outflow: prev.outflow + newExpense.amount,
      balance: prev.balance - newExpense.amount
    }));
    setExpenseForm({ category: '', amount: '', description: '' });
    toast.success('Expense added');
  };

  const handleCashAction = () => {
    if (!cashAction.amount) {
      toast.error('Please enter amount');
      return;
    }
    
    const amount = parseFloat(cashAction.amount);
    
    if (cashAction.type === 'withdrawal') {
      setCashDrawer(prev => ({ ...prev, withdrawals: prev.withdrawals + amount, current: prev.current - amount }));
      setCashFlow(prev => ({ ...prev, outflow: prev.outflow + amount, balance: prev.balance - amount }));
      toast.success(`Withdrawn ₹${amount}`);
    } else if (cashAction.type === 'topup') {
      setCashDrawer(prev => ({ ...prev, topups: prev.topups + amount, current: prev.current + amount }));
      setCashFlow(prev => ({ ...prev, inflow: prev.inflow + amount, balance: prev.balance + amount }));
      toast.success(`Added ₹${amount} to drawer`);
    }
    
    setCashAction({ type: '', amount: '', reason: '' });
  };

  const handleStopOperations = async () => {
    setStopOpsLoading(true);
    try {
      await apiClient.put(`/stores/${user?.store_id}/config`, { accepting_orders: false });
      setAcceptingOrders(false);
      setShowStopOpsConfirm(false);
      toast.success('Operations stopped. Customer app now shows the store as closed.');
    } catch (error) {
      toast.error('Failed to stop operations');
    } finally {
      setStopOpsLoading(false);
    }
  };

  const handleResumeOperations = async () => {
    try {
      await apiClient.put(`/stores/${user?.store_id}/config`, { accepting_orders: true });
      setAcceptingOrders(true);
      toast.success('Operations resumed — now accepting online orders again.');
    } catch (error) {
      toast.error('Failed to resume operations');
    }
  };

  const handleSaveStoreHours = async () => {
    // Validate before saving - an empty open/close time for a day that
    // isn't marked "closed" would silently break the schedule's time
    // comparisons on the backend (an empty close time would make the
    // system think it's always past closing, triggering the auto-close
    // safety net incorrectly).
    const dayLabels = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
    for (const day of Object.keys(storeHours)) {
      const d = storeHours[day];
      if (!d.closed && (!d.open || !d.close)) {
        toast.error(`${dayLabels[day]} needs both an opening and closing time, or mark it as Closed.`);
        return;
      }
    }
    setStoreHoursLoading(true);
    try {
      await apiClient.put(`/stores/${user?.store_id}/config`, { store_timing: storeHours, scheduled_leaves: scheduledLeaves });
      toast.success('Store hours saved — applies to POS, Assisted Order, and online ordering.');
      setShowStoreHoursModal(false);
    } catch (error) {
      toast.error('Failed to save store hours');
    } finally {
      setStoreHoursLoading(false);
    }
  };

  // Converts the VAPID public key (a base64url string from the backend)
  // into the raw byte array format the browser's Push API actually
  // requires for subscribe() — a standard, necessary conversion step.
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
    return outputArray;
  };

  const checkPushStatus = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushSupported(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setPushSubscribed(!!sub);
    } catch (error) {
      console.error('Error checking push status:', error);
    }
  };

  const handleOpenOrderNotifications = async () => {
    await checkPushStatus();
    setShowOrderNotificationsModal(true);
  };

  const handleEnablePushNotifications = async () => {
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notifications permission was not granted — check your browser/phone settings');
        setPushLoading(false);
        return;
      }

      const { data } = await apiClient.get('/push/vapid-public-key');
      if (!data.public_key) {
        toast.error('Notifications are not configured on the server yet');
        setPushLoading(false);
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });

      await apiClient.post('/push/subscribe', subscription.toJSON());
      setPushSubscribed(true);
      toast.success('Order notifications enabled on this device');
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast.error('Could not enable notifications — please try again');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePushNotifications = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await apiClient.post('/push/unsubscribe', null, { params: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
      setPushSubscribed(false);
      toast.success('Order notifications turned off on this device');
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      toast.error('Could not turn off notifications — please try again');
    } finally {
      setPushLoading(false);
    }
  };

  const handleOpenStoreLocation = async () => {
    try {
      const res = await apiClient.get(`/stores/${user?.store_id}`);
      setStoreLat(res.data?.lat != null ? String(res.data.lat) : '');
      setStoreLng(res.data?.lng != null ? String(res.data.lng) : '');
    } catch (error) {
      console.error('Error loading store location:', error);
    }
    setShowStoreLocationModal(true);
  };

  const handleSaveStoreLocation = async () => {
    const lat = parseFloat(storeLat);
    const lng = parseFloat(storeLng);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      toast.error('Enter a valid latitude (-90 to 90)');
      return;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      toast.error('Enter a valid longitude (-180 to 180)');
      return;
    }
    setStoreLocationLoading(true);
    try {
      await apiClient.put(`/stores/${user?.store_id}`, { lat, lng });
      toast.success('Store location saved — delivery distance now calculates automatically');
      setShowStoreLocationModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setStoreLocationLoading(false);
    }
  };

  const handleManualVerifyPhone = async () => {
    const digits = manualVerifyPhone.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Enter a valid 10-digit number');
      return;
    }
    setManualVerifyLoading(true);
    try {
      await apiClient.post('/customer/auth/phone/staff-manual-verify', { phone: digits });
      toast.success(`Phone ${digits} marked as verified. Customer's app will update automatically.`);
      setManualVerifyPhone('');
      setShowVerifyPhoneModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setManualVerifyLoading(false);
    }
  };

  const handleOpenSmsVerifySettings = async () => {
    try {
      const res = await apiClient.get(`/stores/${user?.store_id}`);
      setSmsVerifyNumber(res.data?.config?.sms_verify_number || '');
    } catch (error) {
      console.error('Error loading SMS verify number:', error);
    }
    setShowSmsVerifyModal(true);
  };

  const handleSaveSmsVerifyNumber = async () => {
    const digits = smsVerifyNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Enter a valid 10-digit number');
      return;
    }
    try {
      await apiClient.put(`/stores/${user?.store_id}/config`, {
        sms_verify_number: digits
      });
      toast.success('SMS verification number saved for this store');
      setShowSmsVerifyModal(false);
    } catch (error) {
      console.error('Error saving SMS verify number:', error);
      toast.error('Failed to save');
    }
  };

  const handleSaveTaxConfig = async () => {
    try {
      // Calculate total tax percent from enabled taxes
      const totalPercent = taxConfig.taxes
        .filter(t => t.enabled)
        .reduce((sum, t) => sum + t.percent, 0);
      
      // Use the correct endpoint and format
      // When main tax toggle is OFF, both taxes AND service charge are disabled
      await apiClient.put(`/stores/${user?.store_id}/config`, {
        tax_enabled: taxConfig.enabled,
        tax_percent: totalPercent,
        tax_breakdown: taxConfig.taxes,
        // Service charge follows the main tax toggle
        service_charge_enabled: taxConfig.enabled && taxConfig.serviceCharge?.enabled,
        service_charge_percent: taxConfig.serviceCharge?.percent || 0
      });
      toast.success('Tax & Service Charge configuration saved!');
      setShowModule(null);
    } catch (error) {
      console.error('Tax config error:', error);
      toast.error('Failed to save tax config');
    }
  };

  const handleToggleMenuItem = async (itemId, currentActive) => {
    try {
      await apiClient.put(`/menu/${user?.store_id}/${itemId}`, {
        active: !currentActive
      });
      setMenuItems(menuItems.map(item => 
        item.id === itemId ? { ...item, active: !currentActive } : item
      ));
      toast.success(`Item ${!currentActive ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  // Customer handlers
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || ''
    });
    setShowCustomerForm(true);
  };

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setCustomerForm({ name: '', phone: '', address: '' });
    setShowCustomerForm(true);
  };

  const handleSaveCustomer = async () => {
    try {
      if (!customerForm.phone) {
        toast.error('Phone number is required');
        return;
      }

      if (editingCustomer) {
        // Update existing customer
        await apiClient.put(`/customers/${editingCustomer.id || editingCustomer.customer_id}`, {
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address
        });
        // Update local state
        setCustomers(customers.map(c => 
          (c.phone === editingCustomer.phone) 
            ? { ...c, name: customerForm.name, phone: customerForm.phone, address: customerForm.address }
            : c
        ));
        toast.success('Customer updated!');
      } else {
        // Create new customer
        await apiClient.post('/customers', {
          store_id: user?.store_id,
          name: customerForm.name,
          phone: customerForm.phone,
          address: customerForm.address
        });
        // Reload customers
        loadDashboardData(user?.store_id);
        toast.success('Customer added!');
      }
      
      setShowCustomerForm(false);
      setEditingCustomer(null);
      setCustomerForm({ name: '', phone: '', address: '' });
    } catch (error) {
      console.error('Save customer error:', error);
      toast.error('Failed to save customer');
    }
  };

  // Category handlers
  const handleAddCategory = async (categoryName) => {
    try {
      await apiClient.post('/categories', {
        store_id: user?.store_id,
        name: categoryName
      });
      toast.success(`Category '${categoryName}' created!`);
      loadDashboardData(user?.store_id);
    } catch (error) {
      console.error('Add category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    const itemCount = menuItems.filter(item => item.category === categoryName).length;
    const confirmed = window.confirm(
      `Are you sure you want to DELETE "${categoryName}" category?\n\nThis will PERMANENTLY DELETE ${itemCount} item(s) in this category.\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await apiClient.delete(`/categories/${encodeURIComponent(categoryName)}?store_id=${user?.store_id}`);
      toast.success(`Category '${categoryName}' deleted with ${itemCount} items`);
      loadDashboardData(user?.store_id);
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleDeleteMenuItem = async (itemId, itemName) => {
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE "${itemName}"?\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await apiClient.delete(`/menu/${itemId}`);
      setMenuItems(menuItems.filter(item => item.id !== itemId));
      toast.success(`Item '${itemName}' permanently deleted`);
    } catch (error) {
      console.error('Delete item error:', error);
      toast.error('Failed to delete item');
    }
  };

  // Menu item handlers
  const handleAddMenuItem = () => {
    setEditingMenuItem(null);
    setMenuItemForm({ 
      name: '', 
      category: '', 
      price: '', 
      description: '',
      short_code: '',
      active: true,
      is_combo: false,
      combo_items: []
    });
    setShowMenuItemForm(true);
  };

  const handleEditMenuItem = (item) => {
    setEditingMenuItem(item);
    setMenuItemForm({
      name: item.name || '',
      category: item.category || '',
      price: item.price?.toString() || '',
      description: item.description || '',
      short_code: item.short_code || '',
      active: item.active !== false,
      is_combo: item.is_combo || false,
      combo_items: item.combo_items || []
    });
    setShowMenuItemForm(true);
  };

  const handleSaveMenuItem = async () => {
    if (!menuItemForm.name || !menuItemForm.category || !menuItemForm.price) {
      toast.error('Please fill Name, Category, and Price');
      return;
    }

    const price = parseFloat(menuItemForm.price);
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      if (editingMenuItem) {
        // Update existing item
        await apiClient.put(`/menu/${user?.store_id}/${editingMenuItem.id}`, {
          name: menuItemForm.name,
          category: menuItemForm.category,
          price: price,
          description: menuItemForm.description,
          short_code: menuItemForm.short_code,
          active: menuItemForm.active,
          is_combo: menuItemForm.is_combo,
          combo_items: menuItemForm.combo_items
        });
        setMenuItems(menuItems.map(item => 
          item.id === editingMenuItem.id 
            ? { ...item, ...menuItemForm, price, is_combo: menuItemForm.is_combo, combo_items: menuItemForm.combo_items }
            : item
        ));
        toast.success('Menu item updated!');
      } else {
        // Create new item - generate SKU from name
        const sku = menuItemForm.name.toUpperCase().replace(/[^A-Z0-9]/g, '_').substring(0, 15) + '_' + Date.now().toString(36).toUpperCase();
        const response = await apiClient.post(`/menu?store_id=${user?.store_id}`, {
          sku: sku,
          name: menuItemForm.name,
          category: menuItemForm.category,
          price: price,
          description: menuItemForm.description,
          short_code: menuItemForm.short_code,
          active: menuItemForm.active,
          is_combo: menuItemForm.is_combo,
          combo_items: menuItemForm.combo_items
        });
        if (response.data) {
          setMenuItems([...menuItems, response.data]);
        }
        toast.success('Menu item created!');
      }
      
      setShowMenuItemForm(false);
      setEditingMenuItem(null);
      setMenuItemForm({ name: '', category: '', price: '', description: '', short_code: '', active: true, is_combo: false, combo_items: [] });
    } catch (error) {
      console.error('Save menu item error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save menu item');
    }
  };

  // Discount handlers - COMPREHENSIVE DISCOUNT SYSTEM
  const handleAddDiscount = () => {
    setEditingDiscount(null);
    setDiscountForm({
      name: '',
      type: 'percent',
      allowedModes: ['DINE_IN'],
      categoryDiscounts: {},
      itemDiscounts: {},
      billDiscount: 0
    });
    setShowDiscountForm(true);
  };

  const handleEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountForm({
      name: discount.name,
      type: discount.type || 'percent',
      allowedModes: discount.allowedModes || ['DINE_IN'],
      categoryDiscounts: discount.categoryDiscounts || {},
      itemDiscounts: discount.itemDiscounts || {},
      billDiscount: discount.billDiscount || 0
    });
    setShowDiscountForm(true);
  };

  const handleSaveDiscount = () => {
    if (!discountForm.name) {
      toast.error('Please enter a discount name');
      return;
    }

    if (discountForm.allowedModes.length === 0) {
      toast.error('Please select at least one order mode');
      return;
    }

    // Validate at least one discount type is set
    const hasCategoryDiscount = Object.values(discountForm.categoryDiscounts).some(v => v > 0);
    const hasItemDiscount = Object.values(discountForm.itemDiscounts).some(v => v.percent > 0);
    const hasBillDiscount = discountForm.billDiscount > 0;

    if (!hasCategoryDiscount && !hasItemDiscount && !hasBillDiscount) {
      toast.error('Please set at least one discount (category, item, or bill-level)');
      return;
    }

    if (editingDiscount) {
      // Update existing discount
      setDiscountConfig(prev => ({
        ...prev,
        discounts: prev.discounts.map(d => 
          d.id === editingDiscount.id 
            ? { 
                ...d, 
                name: discountForm.name, 
                type: discountForm.type,
                allowedModes: discountForm.allowedModes,
                categoryDiscounts: discountForm.categoryDiscounts,
                itemDiscounts: discountForm.itemDiscounts,
                billDiscount: discountForm.billDiscount
              }
            : d
        )
      }));
      toast.success('Discount updated!');
    } else {
      // Create new comprehensive discount
      const newDiscount = {
        id: Date.now(),
        name: discountForm.name,
        type: discountForm.type,
        enabled: true,
        allowedModes: discountForm.allowedModes,
        categoryDiscounts: discountForm.categoryDiscounts,
        itemDiscounts: discountForm.itemDiscounts,
        billDiscount: discountForm.billDiscount
      };
      setDiscountConfig(prev => ({
        ...prev,
        discounts: [...prev.discounts, newDiscount]
      }));
      toast.success('Discount created!');
    }

    setShowDiscountForm(false);
    setEditingDiscount(null);
    setDiscountForm({
      name: '',
      type: 'percent',
      allowedModes: ['DINE_IN'],
      categoryDiscounts: {},
      itemDiscounts: {},
      billDiscount: 0
    });
  };

  const handleDeleteDiscount = (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.filter(d => d.id !== discountId)
    }));
    toast.success('Discount deleted');
  };

  const handleToggleDiscount = (discountId) => {
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.map(d => 
        d.id === discountId ? { ...d, enabled: !d.enabled } : d
      )
    }));
  };

  const handleAdjustDiscountValue = (discountId, adjustment) => {
    setDiscountConfig(prev => ({
      ...prev,
      discounts: prev.discounts.map(d => {
        if (d.id !== discountId) return d;
        const newValue = Math.max(0, d.value + adjustment);
        // Cap percentage at 100
        if (d.type === 'percent' && newValue > 100) return d;
        return { ...d, value: newValue };
      })
    }));
  };

  // ALL Dashboard Modules - Unified for full customization
  const allDefaultModules = [
    // Main modules (Row 1)
    { id: 'orders', icon: FileText, label: 'Orders', color: 'bg-blue-500' },
    { id: 'online-orders', icon: Globe, label: 'Online Orders', color: 'bg-green-500' },
    { id: 'kots', icon: Receipt, label: 'KOTs', color: 'bg-orange-500' },
    { id: 'customers', icon: Users, label: 'Customers', color: 'bg-purple-500' },
    { id: 'customer-lookup', icon: Search, label: 'Customer Lookup', color: 'bg-pink-500' },
    { id: 'offers', icon: Gift, label: 'Offers & Discounts', color: 'bg-rose-500' },
    { id: 'cash-flow', icon: Wallet, label: 'Cash Flow', color: 'bg-emerald-500' },
    { id: 'expense', icon: TrendingDown, label: 'Expense', color: 'bg-red-500' },
    { id: 'withdrawal', icon: CreditCard, label: 'Withdrawal', color: 'bg-amber-500' },
    { id: 'cash-topup', icon: PiggyBank, label: 'Cash Top-Up', color: 'bg-teal-500' },
    // Second row
    { id: 'live-view', icon: Eye, label: 'Live View', color: 'bg-green-400' },
    // Third row
    { id: 'day-end', icon: History, label: 'Day End', color: 'bg-fuchsia-500' },
    { id: 'delivery-boys', icon: Bike, label: 'Delivery Boys', color: 'bg-amber-600' },
    // Config modules
    { id: 'assisted-order', icon: ShoppingCart, label: 'Assisted Order', color: 'bg-[#E21836]', capability: 'orders.operate' },
    { id: 'menu-management', icon: Layers, label: 'Menu Management', color: 'bg-[#E21836]', capability: 'menu.manage' },
    { id: 'delivery-settings', icon: Bike, label: 'Delivery Settings', color: 'bg-[#E21836]', capability: 'delivery.config' },
    { id: 'delivery-ops', icon: Bike, label: 'Delivery Operations', color: 'bg-[#E21836]', capability: 'orders.operate' },
    { id: 'print-queue', icon: Printer, label: 'Print Queue', color: 'bg-[#E21836]', capability: 'orders.operate' },
    { id: 'payments', icon: Wallet, label: 'Payments & Refunds', color: 'bg-[#E21836]', capability: 'payments.record' },
    { id: 'payment-settings', icon: CreditCard, label: 'Payment Settings', color: 'bg-[#E21836]', capability: 'payments.config' },
    { id: 'cash-drawer', icon: Wallet, label: 'Cash Drawer', color: 'bg-[#E21836]', capability: 'cash.manage' },
    { id: 'menu', icon: UtensilsCrossed, label: 'Menu', color: 'bg-orange-600' },
    { id: 'categories-config', icon: ListOrdered, label: 'Categories', color: 'bg-violet-600' },
    { id: 'tax', icon: Percent, label: 'Tax', color: 'bg-green-600' },
    { id: 'discount', icon: Percent, label: 'Discount', color: 'bg-purple-600' },
    { id: 'menu-on-off', icon: ToggleLeft, label: 'Menu On/Off', color: 'bg-red-600' },
    { id: 'sms-verify-settings', icon: MessageSquare, label: 'SMS Verify Number', color: 'bg-blue-700', capability: 'menu.manage' },
    { id: 'verify-customer-phone', icon: Phone, label: 'Verify Customer Phone', color: 'bg-emerald-700' },
    { id: 'store-location', icon: MapPin, label: 'Store Location', color: 'bg-indigo-700' },
    { id: 'order-notifications', icon: Bell, label: 'Order Notifications', color: 'bg-rose-700' },
    { id: 'wallet-loyalty', icon: Wallet, label: 'Wallet & Loyalty', color: 'bg-[#E21836]' },
    { id: 'sales-dashboard', icon: TrendingUp, label: "Today's Sales", color: 'bg-emerald-600' },
    { id: 'change-password', icon: Lock, label: 'Change Password', color: 'bg-slate-600' },
  ];

  // Load custom order from localStorage - for ALL modules.
  // Backward-compatible merge: preserve the user's saved order and append any
  // newly-introduced modules so new tiles are never hidden below the fold.
  const [allModuleOrder, setAllModuleOrder] = useState(() => {
    const defaults = allDefaultModules.map(m => m.id);
    const saved = localStorage.getItem('pos_all_module_order');
    if (!saved) return defaults;
    try {
      const savedIds = JSON.parse(saved);
      const merged = [...savedIds, ...defaults.filter(id => !savedIds.includes(id))];
      if (merged.length !== savedIds.length) {
        localStorage.setItem('pos_all_module_order', JSON.stringify(merged));
      }
      return merged;
    } catch {
      return defaults;
    }
  });

  // Sort ALL modules by saved order
  const allModules = [...allDefaultModules]
    .filter((m) => !m.capability || hasCapability(user, m.capability))
    .sort((a, b) => {
    const indexA = allModuleOrder.indexOf(a.id);
    const indexB = allModuleOrder.indexOf(b.id);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  const resetModuleOrder = () => {
    const defaultOrder = allDefaultModules.map(m => m.id);
    setAllModuleOrder(defaultOrder);
    localStorage.setItem('pos_all_module_order', JSON.stringify(defaultOrder));
    toast.success('Dashboard layout reset to default');
  };

  const handleModuleClick = (moduleId) => {
    switch(moduleId) {
      case 'assisted-order':
        navigate('/pos/assisted-order');
        break;
      case 'menu-management':
        navigate('/pos/menu');
        break;
      case 'delivery-settings':
        navigate('/pos/delivery');
        break;
      case 'delivery-ops':
        navigate('/pos/delivery-ops');
        break;
      case 'print-queue':
        navigate('/pos/prints');
        break;
      case 'payments':
        navigate('/pos/payments');
        break;
      case 'payment-settings':
        navigate('/pos/payment-settings');
        break;
      case 'cash-drawer':
        navigate('/pos/cash');
        break;
      case 'orders':
      case 'kots':
      case 'live-view':
        navigate('/pos');
        break;
      case 'online-orders':
        navigate('/pos', { state: { openLiveView: 'online' } });
        break;
      case 'offers':
        setShowOffersManager(true);
        break;
      case 'customer-lookup':
        setShowCustomerLookup(true);
        break;
      case 'menu':
      case 'delivery-boys':
      case 'customers':
      case 'cash-flow':
      case 'expense':
      case 'withdrawal':
      case 'cash-topup':
      case 'tax':
      case 'discount':
      case 'menu-on-off':
      case 'day-end':
      case 'categories-config':
      case 'categories':
        setShowModule(moduleId);
        break;
      case 'sms-verify-settings':
        handleOpenSmsVerifySettings();
        break;
      case 'verify-customer-phone':
        setShowVerifyPhoneModal(true);
        break;
      case 'store-location':
        handleOpenStoreLocation();
        break;
      case 'order-notifications':
        handleOpenOrderNotifications();
        break;
      case 'wallet-loyalty':
        navigate('/pos/wallet-loyalty');
        break;
      case 'sales-dashboard':
        navigate('/pos/sales-dashboard');
        break;
      case 'change-password':
        navigate('/change-password');
        break;
      default:
        toast.info(`${moduleId} module coming soon`);
    }
  };

  // Render unified module grid with DRAG & DROP capability
  const handleDragStart = (e, moduleId) => {
    setDraggedModule(moduleId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', moduleId);
  };

  const handleDragOver = (e, targetModuleId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetModuleId) => {
    e.preventDefault();
    if (!draggedModule || draggedModule === targetModuleId) return;
    
    const currentOrder = [...allModuleOrder];
    const draggedIndex = currentOrder.indexOf(draggedModule);
    const targetIndex = currentOrder.indexOf(targetModuleId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove dragged item and insert at target position
    currentOrder.splice(draggedIndex, 1);
    currentOrder.splice(targetIndex, 0, draggedModule);
    
    setAllModuleOrder(currentOrder);
    localStorage.setItem('pos_all_module_order', JSON.stringify(currentOrder));
    setDraggedModule(null);
    toast.success('Layout updated');
  };

  const handleDragEnd = () => {
    setDraggedModule(null);
  };

  const renderUnifiedDashboard = () => (
    <div className="mb-6">
      {/* Header with Reorder Controls */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-600">Dashboard Modules ({allModules.length} items)</h2>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={isEditMode ? "default" : "outline"}
            onClick={() => setIsEditMode(!isEditMode)}
            className="h-7"
          >
            <GripVertical className="h-3 w-3 mr-1" />
            {isEditMode ? 'Done Editing' : 'Customize Layout'}
          </Button>
          {isEditMode && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={resetModuleOrder}
              className="h-7 text-red-600 border-red-300 hover:bg-red-50"
            >
              Reset to Default
            </Button>
          )}
        </div>
      </div>
      
      {isEditMode && (
        <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-3">
          🖱️ <strong>Drag & Drop</strong> any icon to reorder. Your layout will be saved automatically.
        </p>
      )}
      
      {/* Unified Grid - All modules with drag & drop */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
        {allModules.map((module, index) => (
          <Card 
            key={module.id}
            draggable={isEditMode}
            onDragStart={(e) => isEditMode && handleDragStart(e, module.id)}
            onDragOver={(e) => isEditMode && handleDragOver(e, module.id)}
            onDrop={(e) => isEditMode && handleDrop(e, module.id)}
            onDragEnd={handleDragEnd}
            className={`cursor-pointer hover:shadow-lg transition-all relative
              ${!isEditMode ? 'hover:scale-105' : 'ring-2 ring-blue-200 cursor-grab active:cursor-grabbing'}
              ${draggedModule === module.id ? 'opacity-50 scale-95' : ''}
              ${isEditMode && draggedModule && draggedModule !== module.id ? 'hover:ring-green-400 hover:bg-green-50' : ''}
            `}
            onClick={() => !isEditMode && handleModuleClick(module.id)}
          >
            <CardContent className="p-3 flex flex-col items-center justify-center text-center">
              <div className={`${module.color} p-2 rounded-lg mb-2`}>
                <module.icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] font-medium text-gray-700 leading-tight">{module.label}</span>
              
              {/* Drag handle indicator in edit mode */}
              {isEditMode && (
                <GripVertical className="absolute top-1 right-1 h-3 w-3 text-gray-400" />
              )}
              
              {/* Position indicator in edit mode */}
              {isEditMode && (
                <span className="absolute -top-1 -left-1 bg-blue-500 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
                  {index + 1}
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#C41E3A] text-white px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="h-6 w-6 flex-shrink-0" />
            <div>
              <h1 className="font-bold text-lg">SSPICE POS Dashboard</h1>
              <span className="text-xs opacity-80">(Designed by Prashant B)</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              size="sm"
              data-testid="stop-operations-toggle"
              className={(acceptingOrders ? 'bg-green-600 hover:bg-green-700' : 'bg-red-700 hover:bg-red-800 animate-pulse') + ' flex-1 sm:flex-none min-w-0'}
              onClick={() => acceptingOrders ? setShowStopOpsConfirm(true) : handleResumeOperations()}
            >
              <span className="truncate">
                {acceptingOrders ? '🟢 Store Open — Stop Operations' : '🔴 Store Closed — Resume Operations'}
              </span>
            </Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 flex-shrink-0" onClick={() => setShowStoreHoursModal(true)}>
              🕐 Store Hours
            </Button>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 flex-shrink-0" onClick={() => navigate('/pos')}>
              New Order
            </Button>
            <Button size="sm" variant="outline" className="text-white border-white hover:bg-red-700 flex-shrink-0" onClick={() => navigate('/login')}>
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4">
        {renderUnifiedDashboard()}
      </div>

      {/* Delivery Boys Modal */}
      <Dialog open={showModule === 'delivery-boys'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bike className="h-5 w-5" /> Delivery Boys Management
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {riders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No riders found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {riders.map((rider) => (
                    <TableRow key={rider.id}>
                      <TableCell className="font-medium">{rider.name}</TableCell>
                      <TableCell>{rider.phone}</TableCell>
                      <TableCell>
                        <Badge className={rider.active ? 'bg-green-500' : 'bg-gray-500'}>
                          {rider.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => toast.info('Track rider coming soon')}>
                          Track
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Customers Modal */}
      <Dialog open={showModule === 'customers'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Customer Management
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await apiClient.get('/customers/export', { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'sspice_customers_full.csv';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toast.error('Failed to export customers');
                    }
                  }}
                  data-testid="export-customers-csv"
                >
                  <Download className="h-4 w-4 mr-1" /> Export to Excel
                </Button>
                <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddCustomer}>
                  <Plus className="h-4 w-4 mr-1" /> Add Customer
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {customers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No customers found
                <Button variant="outline" className="mt-4 block mx-auto" onClick={handleAddCustomer}>
                  <Plus className="h-4 w-4 mr-1" /> Add First Customer
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Last Order</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{customer.name || 'N/A'}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{customer.address || 'N/A'}</TableCell>
                      <TableCell>{customer.totalOrders}</TableCell>
                      <TableCell className="text-xs">{new Date(customer.lastOrder).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7"
                          onClick={() => handleEditCustomer(customer)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Customer Edit/Add Form Modal */}
      <Dialog open={showCustomerForm} onOpenChange={() => setShowCustomerForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name</Label>
              <Input
                placeholder="Enter full name"
                value={customerForm.name}
                onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                maxLength={10}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                placeholder="Full address"
                value={customerForm.address}
                onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerForm(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleSaveCustomer}>
              {editingCustomer ? 'Save Changes' : 'Add Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Flow Modal */}
      <Dialog open={showModule === 'cash-flow'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Today&apos;s Cash Flow
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-sm text-green-600">Total Inflow</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(cashFlow.inflow)}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <p className="text-sm text-red-600">Total Outflow</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(cashFlow.outflow)}</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600">Net Balance</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(cashFlow.balance)}</p>
              </CardContent>
            </Card>
            <div className="text-xs text-gray-500 text-center">
              Based on {orders.filter(o => new Date(o.created_at).toDateString() === new Date().toDateString()).length} orders today
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={showModule === 'expense'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5" /> Expense Management
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingredients">Ingredients</SelectItem>
                    <SelectItem value="utilities">Utilities</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input 
                  type="number" 
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                placeholder="Enter description"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
              />
            </div>
            <Button className="w-full bg-red-500 hover:bg-red-600" onClick={handleAddExpense}>
              <Plus className="h-4 w-4 mr-2" /> Add Expense
            </Button>
            
            {expenses.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-2">Today&apos;s Expenses</h4>
                <ScrollArea className="max-h-40">
                  {expenses.map((exp) => (
                    <div key={exp.id} className="flex justify-between items-center p-2 bg-gray-50 rounded mb-1">
                      <div>
                        <p className="font-medium text-sm">{exp.category}</p>
                        <p className="text-xs text-gray-500">{exp.description}</p>
                      </div>
                      <span className="font-bold text-red-600">{formatCurrency(exp.amount)}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdrawal Modal */}
      <Dialog open={showModule === 'withdrawal'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" /> Cash Withdrawal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input 
                type="number" 
                placeholder="Enter amount"
                value={cashAction.amount}
                onChange={(e) => setCashAction({...cashAction, type: 'withdrawal', amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                placeholder="Reason for withdrawal"
                value={cashAction.reason}
                onChange={(e) => setCashAction({...cashAction, reason: e.target.value})}
              />
            </div>
            <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={handleCashAction}>
              <Minus className="h-4 w-4 mr-2" /> Withdraw
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash Top-Up Modal */}
      <Dialog open={showModule === 'cash-topup'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5" /> Cash Top-Up
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input 
                type="number" 
                placeholder="Enter amount"
                value={cashAction.amount}
                onChange={(e) => setCashAction({...cashAction, type: 'topup', amount: e.target.value})}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                placeholder="Reason for top-up"
                value={cashAction.reason}
                onChange={(e) => setCashAction({...cashAction, reason: e.target.value})}
              />
            </div>
            <Button className="w-full bg-teal-500 hover:bg-teal-600" onClick={handleCashAction}>
              <Plus className="h-4 w-4 mr-2" /> Add Cash
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tax Configuration Modal */}
      <Dialog open={showModule === 'tax'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" /> Tax & Service Charge Configuration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Main Tax Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
              <div>
                <Label className="font-semibold">Enable Taxes & Charges</Label>
                <p className="text-xs text-gray-500">When OFF, no taxes or service charge will apply</p>
              </div>
              <Switch 
                checked={taxConfig.enabled}
                onCheckedChange={(checked) => setTaxConfig({...taxConfig, enabled: checked})}
              />
            </div>
            
            {taxConfig.enabled && (
              <div className="space-y-3">
                {/* Tax Items */}
                <p className="text-sm font-medium text-gray-600">Tax Breakdown</p>
                {taxConfig.taxes.map((tax, idx) => (
                  <div key={tax.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                    <Switch 
                      checked={tax.enabled}
                      onCheckedChange={(checked) => {
                        const newTaxes = [...taxConfig.taxes];
                        newTaxes[idx].enabled = checked;
                        setTaxConfig({...taxConfig, taxes: newTaxes});
                      }}
                    />
                    <div className="flex-1">
                      <Input 
                        value={tax.name}
                        onChange={(e) => {
                          const newTaxes = [...taxConfig.taxes];
                          newTaxes[idx].name = e.target.value;
                          setTaxConfig({...taxConfig, taxes: newTaxes});
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={tax.percent}
                        onChange={(e) => {
                          const newTaxes = [...taxConfig.taxes];
                          newTaxes[idx].percent = parseFloat(e.target.value) || 0;
                          setTaxConfig({...taxConfig, taxes: newTaxes});
                        }}
                        className="h-8 text-sm"
                      />
                    </div>
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                ))}
                
                <div className="flex justify-between p-3 bg-blue-50 rounded">
                  <span className="font-medium">Total Tax</span>
                  <span className="font-bold text-blue-600">
                    {taxConfig.taxes.filter(t => t.enabled).reduce((sum, t) => sum + t.percent, 0)}%
                  </span>
                </div>

                {/* Service Charge Section */}
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-600 mb-2">Service Charge</p>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded">
                    <Switch 
                      checked={taxConfig.serviceCharge?.enabled || false}
                      onCheckedChange={(checked) => setTaxConfig({
                        ...taxConfig, 
                        serviceCharge: { ...taxConfig.serviceCharge, enabled: checked }
                      })}
                    />
                    <span className="flex-1 text-sm">Service Charge</span>
                    <div className="w-20">
                      <Input 
                        type="number"
                        value={taxConfig.serviceCharge?.percent || 0}
                        onChange={(e) => setTaxConfig({
                          ...taxConfig,
                          serviceCharge: { ...taxConfig.serviceCharge, percent: parseFloat(e.target.value) || 0 }
                        })}
                        className="h-8 text-sm"
                        disabled={!taxConfig.serviceCharge?.enabled}
                      />
                    </div>
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>
            )}
            
            {!taxConfig.enabled && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                <p className="text-sm text-yellow-800">⚠️ All taxes and service charges are currently DISABLED</p>
                <p className="text-xs text-yellow-600 mt-1">Bills will show base prices only</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModule(null)}>Cancel</Button>
            <Button className="bg-green-600" onClick={handleSaveTaxConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Configuration Modal - COMPREHENSIVE SYSTEM */}
      <Dialog open={showModule === 'discount'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Percent className="h-5 w-5" /> Discount Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddDiscount}>
                <Plus className="h-4 w-4 mr-1" /> Create Discount
              </Button>
            </DialogTitle>
            <p className="text-xs text-gray-500">Configure discounts with category, item, and order mode rules</p>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh]">
            <div className="space-y-3 py-4">
              {discountConfig.discounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Percent className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No discounts configured</p>
                  <Button variant="outline" className="mt-3" onClick={handleAddDiscount}>
                    <Plus className="h-4 w-4 mr-1" /> Create First Discount
                  </Button>
                </div>
              ) : (
                discountConfig.discounts.map((discount) => (
                  <div key={discount.id} className={`p-4 rounded-lg border ${discount.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Switch 
                          checked={discount.enabled}
                          onCheckedChange={() => handleToggleDiscount(discount.id)}
                        />
                        <div>
                          <p className="font-bold text-sm">{discount.name}</p>
                          <Badge variant="outline" className="mt-1 text-[10px]">
                            {discount.type === 'percent' ? 'Percentage' : 'Fixed'}
                          </Badge>
                        </div>
                      </div>
                      <Badge variant={discount.enabled ? 'default' : 'outline'} className={discount.enabled ? 'bg-green-500' : ''}>
                        {discount.enabled ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    {/* Order Modes */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {ORDER_MODES.map(mode => {
                        const isAllowed = discount.allowedModes?.includes(mode.id);
                        return (
                          <span key={mode.id} className={`text-[10px] px-2 py-0.5 rounded-full ${isAllowed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500 line-through'}`}>
                            {mode.icon} {mode.label}
                          </span>
                        );
                      })}
                    </div>
                    
                    {/* Discount Summary */}
                    <div className="bg-white rounded-lg p-2 border text-xs space-y-1">
                      {discount.categoryDiscounts && Object.keys(discount.categoryDiscounts).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-gray-500">Categories:</span>
                          {Object.entries(discount.categoryDiscounts).map(([cat, pct]) => (
                            <Badge key={cat} variant="outline" className="text-[10px]">{cat}: {pct}%</Badge>
                          ))}
                        </div>
                      )}
                      {discount.itemDiscounts && Object.keys(discount.itemDiscounts).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-gray-500">Items:</span>
                          {Object.entries(discount.itemDiscounts).map(([id, item]) => (
                            <Badge key={id} variant="secondary" className="text-[10px]">{item.name}: {item.percent}%</Badge>
                          ))}
                        </div>
                      )}
                      {discount.billDiscount > 0 && (
                        <div>
                          <span className="text-gray-500">Bill Fallback:</span>
                          <Badge variant="outline" className="ml-1 text-[10px]">{discount.billDiscount}%</Badge>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEditDiscount(discount)}>
                        <Settings className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteDiscount(discount.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => { setShowModule(null); toast.success('Discount configuration saved!'); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COMPREHENSIVE Add/Edit Discount Form Dialog */}
      <Dialog open={showDiscountForm} onOpenChange={() => setShowDiscountForm(false)}>
        <DialogContent className="max-w-xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingDiscount ? 'Edit Discount' : 'Create New Discount'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-5 py-4">
              {/* Discount Name */}
              <div>
                <Label className="text-sm font-medium">Discount Name *</Label>
                <Input 
                  placeholder="e.g., Weekend Special, Happy Hour"
                  value={discountForm.name}
                  onChange={(e) => setDiscountForm({ ...discountForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {/* Discount Type */}
              <div>
                <Label className="text-sm font-medium">Discount Type *</Label>
                <Select value={discountForm.type} onValueChange={(v) => setDiscountForm({ ...discountForm, type: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ORDER MODE RESTRICTIONS */}
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <Label className="text-sm font-medium">Applies To (Order Modes) *</Label>
                <p className="text-xs text-gray-500 mb-2">Select which order types this discount applies to</p>
                <div className="flex flex-wrap gap-2">
                  {ORDER_MODES.map(mode => {
                    const isAllowed = discountForm.allowedModes?.includes(mode.id);
                    return (
                      <Button
                        key={mode.id}
                        type="button"
                        size="sm"
                        variant={isAllowed ? 'default' : 'outline'}
                        className={`h-8 ${isAllowed ? 'bg-green-500 hover:bg-green-600' : 'text-red-500 border-red-200'}`}
                        onClick={() => {
                          const newModes = isAllowed
                            ? discountForm.allowedModes.filter(m => m !== mode.id)
                            : [...(discountForm.allowedModes || []), mode.id];
                          setDiscountForm({ ...discountForm, allowedModes: newModes });
                        }}
                      >
                        {mode.icon} {mode.label}
                        {isAllowed ? ' ✓' : ' ✗'}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  ⚠️ Swiggy/Zomato orders have higher menu prices. Disable discounts for aggregators to protect revenue.
                </p>
              </div>

              {/* CATEGORY-WISE DISCOUNTS */}
              <div className="border rounded-lg p-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  📁 Category Discounts
                  <span className="text-xs text-gray-400 font-normal">(Multiple categories allowed)</span>
                </Label>
                <p className="text-xs text-gray-500 mb-3">Set custom discount % for each category</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {categories.map((cat, idx) => {
                    const catName = typeof cat === 'string' ? cat : cat.name;
                    const currentPct = discountForm.categoryDiscounts?.[catName] || 0;
                    return (
                      <div key={catName || idx} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                        <span className="text-sm font-medium">{catName}</span>
                        <div className="flex items-center gap-1">
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            value={currentPct || ''}
                            placeholder="0"
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setDiscountForm(prev => ({
                                ...prev,
                                categoryDiscounts: {
                                  ...prev.categoryDiscounts,
                                  [catName]: val
                                }
                              }));
                            }}
                            className="w-20 h-8 text-center"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ITEM-LEVEL DISCOUNTS */}
              <div className="border rounded-lg p-3">
                <Label className="text-sm font-medium flex items-center gap-2">
                  🏷️ Item Discounts
                  <span className="text-xs text-gray-400 font-normal">(Overrides category discount)</span>
                </Label>
                <p className="text-xs text-gray-500 mb-3">Set specific discount for individual items</p>
                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                  {menuItems.slice(0, 20).map((item) => {
                    const itemDiscount = discountForm.itemDiscounts?.[item.id];
                    const currentPct = itemDiscount?.percent || 0;
                    return (
                      <div key={item.id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium truncate block">{item.name}</span>
                          <span className="text-[10px] text-gray-400">{item.category} • ₹{item.price}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            value={currentPct || ''}
                            placeholder="0"
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setDiscountForm(prev => ({
                                ...prev,
                                itemDiscounts: {
                                  ...prev.itemDiscounts,
                                  [item.id]: { name: item.name, percent: val }
                                }
                              }));
                            }}
                            className="w-20 h-8 text-center"
                          />
                          <span className="text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    );
                  })}
                  {menuItems.length > 20 && (
                    <p className="text-xs text-gray-400 text-center py-2">Showing first 20 items...</p>
                  )}
                </div>
              </div>

              {/* OVERALL/FALLBACK BILL DISCOUNT */}
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <Label className="text-sm font-medium">Overall Bill Discount (Fallback)</Label>
                <p className="text-xs text-gray-500 mb-2">Only applies if item has no category or item-specific discount</p>
                <div className="flex items-center gap-2">
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    value={discountForm.billDiscount || ''}
                    placeholder="0"
                    onChange={(e) => setDiscountForm({ ...discountForm, billDiscount: parseFloat(e.target.value) || 0 })}
                    className="w-24 h-8 text-center"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              {/* PRIORITY RULE INFO */}
              <div className="bg-gray-100 p-3 rounded-lg text-xs text-gray-600">
                <p className="font-medium mb-1">📋 Discount Priority Rule:</p>
                <p>Item Discount → Category Discount → Bill Discount</p>
                <p className="text-gray-400 mt-1">Item-level overrides category, category overrides bill</p>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountForm(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={handleSaveDiscount}>
              {editingDiscount ? 'Update' : 'Create'} Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu On/Off Modal */}
      <Dialog open={showModule === 'menu-on-off'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ToggleLeft className="h-5 w-5" /> Menu Items On/Off
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">
            🔘 Toggle items on/off. OFF items show in grayscale on POS. 🗑️ Delete permanently removes items.
          </p>
          <ScrollArea className="max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Toggle</TableHead>
                  <TableHead>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {menuItems.map((item) => (
                  <TableRow key={item.id} className={!item.active ? 'bg-gray-100 opacity-60' : ''}>
                    <TableCell className={`font-medium ${!item.active ? 'text-gray-400' : ''}`}>
                      {item.name}
                    </TableCell>
                    <TableCell className={!item.active ? 'text-gray-400' : ''}>{item.category}</TableCell>
                    <TableCell className={!item.active ? 'text-gray-400' : ''}>{formatCurrency(item.price)}</TableCell>
                    <TableCell>
                      <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                        {item.active ? 'ON' : 'OFF'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch 
                        checked={item.active}
                        onCheckedChange={() => handleToggleMenuItem(item.id, item.active)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="h-7"
                        onClick={() => handleDeleteMenuItem(item.id, item.name)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Day End Modal */}
      <Dialog open={showModule === 'day-end'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Day End & Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Day Time Settings */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold text-blue-800 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Day Operation Times
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Day Start Time</Label>
                    <Input 
                      type="time" 
                      value={daySettings.day_start_time || '09:00'}
                      onChange={(e) => setDaySettings({...daySettings, day_start_time: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Day End Time</Label>
                    <Input 
                      type="time" 
                      value={daySettings.day_end_time || '23:00'}
                      onChange={(e) => setDaySettings({...daySettings, day_end_time: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={async () => {
                    try {
                      await apiClient.post('/day-operations/settings', {
                        store_id: user?.store_id,
                        day_start_time: daySettings.day_start_time,
                        day_end_time: daySettings.day_end_time
                      });
                      toast.success('Day times saved!');
                    } catch (e) {
                      toast.error('Failed to save settings');
                    }
                  }}
                >
                  Save Day Times
                </Button>
              </CardContent>
            </Card>

            {/* Day Status */}
            <Card className={`border-2 ${daySettings.is_day_started ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Day Status</p>
                    <p className={`text-sm ${daySettings.is_day_started ? 'text-green-600' : 'text-gray-600'}`}>
                      {daySettings.is_day_started ? '🟢 Day is ACTIVE' : '⚪ Day not started'}
                    </p>
                  </div>
                  {!daySettings.is_day_started && (
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          const res = await apiClient.post('/day-operations/start-day', { store_id: user?.store_id });
                          setDaySettings({...daySettings, is_day_started: true});
                          if (res.data?.scheduled_leave_warning) {
                            toast.warning(res.data.scheduled_leave_warning, { duration: 8000 });
                          } else {
                            toast.success('Day started!');
                          }
                        } catch (e) {
                          toast.error('Failed to start day');
                        }
                      }}
                    >
                      Start Day
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Today's Summary */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold flex items-center gap-2">
                  📅 Today&apos;s Summary 
                  <span className="text-xs text-gray-500 font-normal">({new Date().toLocaleDateString('en-IN')})</span>
                </p>
                {(() => {
                  // Filter only today's orders - compare by date string (YYYY-MM-DD) to avoid timezone issues
                  const todayStr = new Date().toISOString().split('T')[0];
                  const todayOrders = orders.filter(o => {
                    if (!o.created_at) return false;
                    // Extract just the date part from created_at (handles both ISO and other formats)
                    const orderDateStr = o.created_at.substring(0, 10);
                    return orderDateStr === todayStr;
                  });
                  const todayPending = todayOrders.filter(o => !['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status));
                  const todayCancelled = todayOrders.filter(o => o.status === 'CANCELLED');
                  const todayCompleted = todayOrders.filter(o => ['SERVED', 'PICKED_UP', 'DELIVERED'].includes(o.status));
                  const todaySales = todayCompleted.reduce((sum, o) => sum + (o.total || 0), 0);
                  
                  return (
                    <>
                      <div className="flex justify-between">
                        <span>Total Orders</span>
                        <span className="font-bold">{todayOrders.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Completed Orders</span>
                        <span className="font-bold text-green-600">{todayCompleted.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pending Orders</span>
                        <span className="font-bold text-orange-600">{todayPending.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cancelled Orders</span>
                        <span className="font-bold text-red-600">{todayCancelled.length}</span>
                      </div>
                      <div className="border-t pt-3 flex justify-between">
                        <span className="font-medium">Today&apos;s Sales</span>
                        <span className="font-bold text-xl text-green-600">{formatCurrency(todaySales)}</span>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800 font-medium">⚠️ End of Day Warning</p>
              <p className="text-xs text-yellow-700 mt-1">
                Closing the day will:
                <br/>• Auto-complete all READY orders
                <br/>• Cancel all pending/preparing orders  
                <br/>• Archive all cancelled orders
                <br/>• Clear the Kitchen Display
                <br/>• Auto-download Excel report
              </p>
            </div>
            
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700" 
              onClick={async () => {
                // Filter only today's orders
                const today = new Date().toDateString();
                const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today);
                const pendingCount = todayOrders.filter(o => 
                  !['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status)
                ).length;
                const cancelledCount = todayOrders.filter(o => o.status === 'CANCELLED').length;
                
                if (!confirm(`Are you sure you want to close the day?\n\nToday's Summary:\n• ${todayOrders.length} total order(s)\n• ${pendingCount} pending order(s) will be processed\n• ${cancelledCount} cancelled order(s) will be archived\n\nThis action cannot be undone.`)) {
                  return;
                }
                
                try {
                  // Call day-end API
                  const response = await apiClient.post('/day-operations/end-day', {
                    store_id: user?.store_id
                  });
                  
                  const summary = response.data.summary;
                  toast.success(`Day closed! ${summary.orders_auto_closed} completed, ${summary.orders_auto_cancelled} cancelled, ${summary.orders_archived} archived.`);
                  
                  // Auto-download Excel report
                  try {
                    const reportResponse = await apiClient.get(`/day-operations/export-report/${user?.store_id}`, {
                      responseType: 'blob'
                    });
                    
                    // Create download link
                    const url = window.URL.createObjectURL(new Blob([reportResponse.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Day_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    
                    toast.success('Report downloaded!');
                  } catch (reportError) {
                    console.error('Report download error:', reportError);
                    toast.error('Day closed but report download failed');
                  }
                  
                  // Refresh data
                  loadDashboardData(user?.store_id);
                  setShowModule(null);
                } catch (error) {
                  console.error('Day end error:', error);
                  toast.error(error.response?.data?.detail || 'Failed to close day');
                }
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Close Day & Download Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Day Start Popup on First Login */}
      <Dialog open={showDayStartPopup} onOpenChange={setShowDayStartPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Sun className="h-5 w-5" /> Start Your Day
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-lg font-medium">Please set your day operation times</p>
              <p className="text-sm text-gray-500 mt-1">Configure when your business day starts and ends</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Day Start Time</Label>
                <Input 
                  type="time" 
                  value={daySettings.day_start_time || '09:00'}
                  onChange={(e) => setDaySettings({...daySettings, day_start_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Day End Time</Label>
                <Input 
                  type="time" 
                  value={daySettings.day_end_time || '23:00'}
                  onChange={(e) => setDaySettings({...daySettings, day_end_time: e.target.value})}
                />
              </div>
            </div>
            
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={async () => {
                try {
                  // Save settings
                  await apiClient.post('/day-operations/settings', {
                    store_id: user?.store_id,
                    day_start_time: daySettings.day_start_time,
                    day_end_time: daySettings.day_end_time
                  });
                  
                  // Start day
                  const startRes = await apiClient.post('/day-operations/start-day', { store_id: user?.store_id });
                  
                  setDaySettings({...daySettings, is_day_started: true});
                  setShowDayStartPopup(false);
                  if (startRes.data?.scheduled_leave_warning) {
                    toast.warning(startRes.data.scheduled_leave_warning, { duration: 8000 });
                  } else {
                    toast.success('Day started successfully!');
                  }
                } catch (e) {
                  toast.error('Failed to start day');
                }
              }}
            >
              <Sun className="h-4 w-4 mr-2" />
              Save & Start Day
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowDayStartPopup(false)}
            >
              Remind Me Later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Management Modal */}
      <Dialog open={showModule === 'menu'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5" /> Menu Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={handleAddMenuItem}>
                <Plus className="h-4 w-4 mr-1" /> Add New Item
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="all">All Items ({menuItems.length})</TabsTrigger>
              {[...new Set(menuItems.map(item => item.category).filter(Boolean))].sort().map(cat => (
                <TabsTrigger key={cat} value={cat}>
                  {cat} ({menuItems.filter(i => i.category === cat).length})
                </TabsTrigger>
              ))}
            </TabsList>
            
            <TabsContent value="all">
              <ScrollArea className="h-[50vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {menuItems.map((item) => (
                      <TableRow key={item.id} className={!item.active ? 'bg-gray-50 opacity-60' : ''}>
                        <TableCell className="font-medium">
                          {item.name}
                          {item.is_combo && (
                            <Badge className="ml-2 bg-purple-500 text-xs">
                              <Package className="h-3 w-3 mr-1" /> Combo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{formatCurrency(item.price)}</TableCell>
                        <TableCell className="font-mono text-xs">{item.short_code || '-'}</TableCell>
                        <TableCell>
                          <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                            {item.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7" onClick={() => handleEditMenuItem(item)}>
                              <Settings className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7" onClick={() => handleToggleMenuItem(item.id, item.active)}>
                              <ToggleLeft className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7" onClick={() => handleDeleteMenuItem(item.id, item.name)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
            
            {[...new Set(menuItems.map(item => item.category).filter(Boolean))].sort().map(cat => (
              <TabsContent key={cat} value={cat}>
                <ScrollArea className="h-[50vh]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {menuItems.filter(i => i.category === cat).map((item) => (
                        <TableRow key={item.id} className={!item.active ? 'bg-gray-50 opacity-60' : ''}>
                          <TableCell className="font-medium">
                            {item.name}
                            {item.is_combo && (
                              <Badge className="ml-2 bg-purple-500 text-xs">
                                <Package className="h-3 w-3 mr-1" /> Combo
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{formatCurrency(item.price)}</TableCell>
                          <TableCell className="font-mono text-xs">{item.short_code || '-'}</TableCell>
                          <TableCell>
                            <Badge className={item.active ? 'bg-green-500' : 'bg-gray-400'}>
                              {item.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-7" onClick={() => handleEditMenuItem(item)}>
                                <Settings className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline" className="h-7" onClick={() => handleToggleMenuItem(item.id, item.active)}>
                                <ToggleLeft className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7" onClick={() => handleDeleteMenuItem(item.id, item.name)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Menu Item Form Dialog */}
      <Dialog open={showMenuItemForm} onOpenChange={() => setShowMenuItemForm(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMenuItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Item Name *</Label>
              <Input 
                placeholder="e.g., Margherita Pizza"
                value={menuItemForm.name}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, name: e.target.value })}
              />
            </div>

            <div>

              <Label>Category *</Label>

              <Select 

                value={menuItemForm.category_id} 

                onValueChange={(v) => setMenuItemForm({ ...menuItemForm, category_id: v })}

              >

                <SelectTrigger>

                  <SelectValue placeholder="Select category" />

                </SelectTrigger>

                <SelectContent>

                  {categories.map(cat => (

                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>

                  ))}

                </SelectContent>

              </Select>
              </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹) *</Label>
                <Input 
                  type="number"
                  placeholder="99"
                  value={menuItemForm.variants[0].price}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, variants: [{ ...menuItemForm.variants[0], price: e.target.value }] })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Short Code</Label>
                <Input 
                  placeholder="e.g., MP01"
                  value={menuItemForm.short_code}
                  onChange={(e) => setMenuItemForm({ ...menuItemForm, short_code: e.target.value.toUpperCase() })}
                  maxLength={6}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                placeholder="Item description (optional)"
                value={menuItemForm.description}
                onChange={(e) => setMenuItemForm({ ...menuItemForm, description: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={menuItemForm.active}
                onCheckedChange={(checked) => setMenuItemForm({ ...menuItemForm, active: checked })}
              />
              <Label>Active (available for ordering)</Label>
            </div>
            
            {/* Combo Section */}
            <div className="border-t pt-4 mt-4">
              {(menuItemForm.category?.toLowerCase() === 'combo' || menuItemForm.category?.toLowerCase() === 'combos') && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-purple-800 font-medium">🎁 Combo Category Selected</p>
                  <p className="text-xs text-purple-600 mt-1">
                    Items in the Combo category are automatically treated as combo/bundle items. 
                    Add the items included in this combo below.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <Switch 
                  checked={menuItemForm.is_combo}
                  onCheckedChange={(checked) => setMenuItemForm({ ...menuItemForm, is_combo: checked, combo_items: checked ? menuItemForm.combo_items : [] })}
                />
                <Label className="flex items-center gap-1">
                  <Package className="h-4 w-4" /> 
                  This is a Combo/Bundle
                </Label>
              </div>
              
              {menuItemForm.is_combo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-medium text-blue-800">Combo Items</p>
                    <Badge variant="outline" className="bg-white">
                      {menuItemForm.combo_items.length} items
                    </Badge>
                  </div>
                  
                  {/* Add Item to Combo */}
                  <div className="flex gap-2">
                    <Select 
                      onValueChange={(itemId) => {
                        const selectedItem = menuItems.find(i => i.id === itemId);
                        if (selectedItem && !menuItemForm.combo_items.find(c => c.item_id === itemId)) {
                          setMenuItemForm({
                            ...menuItemForm,
                            combo_items: [
                              ...menuItemForm.combo_items,
                              { item_id: itemId, item_name: selectedItem.name, quantity: 1 }
                            ]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1 bg-white">
                        <SelectValue placeholder="Add item to combo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {menuItems
                          .filter(i => i.active && !i.is_combo && !menuItemForm.combo_items.find(c => c.item_id === i.id))
                          .map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} - ₹{item.price}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Combo Items List */}
                  {menuItemForm.combo_items.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {menuItemForm.combo_items.map((comboItem, idx) => (
                        <div key={comboItem.item_id} className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="text-sm font-medium truncate flex-1">{comboItem.item_name}</span>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                const updated = [...menuItemForm.combo_items];
                                if (updated[idx].quantity > 1) {
                                  updated[idx].quantity -= 1;
                                  setMenuItemForm({ ...menuItemForm, combo_items: updated });
                                }
                              }}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-sm font-bold">{comboItem.quantity}</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                const updated = [...menuItemForm.combo_items];
                                updated[idx].quantity += 1;
                                setMenuItemForm({ ...menuItemForm, combo_items: updated });
                              }}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => {
                                setMenuItemForm({
                                  ...menuItemForm,
                                  combo_items: menuItemForm.combo_items.filter((_, i) => i !== idx)
                                });
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-blue-600 text-center py-2">
                      No items added yet. Select items above to build your combo.
                    </p>
                  )}
                  
                  {/* Individual Items Price vs Combo Price */}
                  {menuItemForm.combo_items.length > 0 && (
                    <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                      <div className="flex justify-between">
                        <span>Individual items total:</span>
                        <span className="line-through">
                          ₹{menuItemForm.combo_items.reduce((sum, ci) => {
                            const item = menuItems.find(m => m.id === ci.item_id);
                            return sum + (item?.price || 0) * ci.quantity;
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>Combo price:</span>
                        <span className="text-green-700">₹{menuItemForm.price || '0.00'}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuItemForm(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={handleSaveMenuItem}>
              {editingMenuItem ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories Management Modal */}
      <Dialog open={showModule === 'categories' || showModule === 'categories-config'} onOpenChange={() => setShowModule(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ListOrdered className="h-5 w-5" /> Category Management
              </span>
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => {
                const name = prompt('Enter new category name:');
                if (name && name.trim()) {
                  handleAddCategory(name.trim());
                }
              }}>
                <Plus className="h-4 w-4 mr-1" /> Add Category
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {(() => {
              // Use categories from API (includes both menu item categories and custom categories)
              const allCategories = categories.length > 0 ? categories : [];
              return allCategories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No categories found
                  <Button variant="outline" className="mt-4 block mx-auto" onClick={() => {
                    const name = prompt('Enter new category name:');
                    if (name && name.trim()) {
                      handleAddCategory(name.trim());
                    }
                  }}>
                    <Plus className="h-4 w-4 mr-1" /> Add First Category
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category Name</TableHead>
                      <TableHead>Items Count</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allCategories.map((category) => {
                      const itemCount = menuItems.filter(item => item.category === category).length;
                      return (
                        <TableRow key={category}>
                          <TableCell className="font-medium">{category}</TableCell>
                          <TableCell>{itemCount} items</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              className="h-7"
                              onClick={() => handleDeleteCategory(category)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
          </ScrollArea>
          <div className="text-xs text-gray-500 mt-2">
            ⚠️ Deleting a category will permanently remove all items in that category.
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Verify Number Settings Modal */}
      <Dialog open={showSmsVerifyModal} onOpenChange={setShowSmsVerifyModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" /> SMS Verify Number
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Receiving number for this store</Label>
            <Input
              placeholder="10-digit number"
              value={smsVerifyNumber}
              onChange={(e) => setSmsVerifyNumber(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
            />
            <p className="text-xs text-gray-500">
              Customers verifying their phone will be told to send an SMS to this number.
              Leave blank to use the global default.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSmsVerifyModal(false)}>Cancel</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={handleSaveSmsVerifyNumber}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Customer Phone Verification Modal (fallback if SMS doesn't arrive) */}
      <Dialog open={showVerifyPhoneModal} onOpenChange={setShowVerifyPhoneModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" /> Verify Customer Phone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-gray-500">
              Use this only if the customer's verification SMS didn't arrive
              (e.g. store phone briefly offline). Confirm their number verbally
              before verifying.
            </p>
            <Label>Customer's phone number</Label>
            <Input
              placeholder="10-digit number"
              value={manualVerifyPhone}
              onChange={(e) => setManualVerifyPhone(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerifyPhoneModal(false)}>Cancel</Button>
            <Button
              className="bg-emerald-700 hover:bg-emerald-800"
              onClick={handleManualVerifyPhone}
              disabled={manualVerifyLoading}
            >
              {manualVerifyLoading ? 'Verifying...' : 'Mark Verified'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stop Operations Confirmation Modal */}
      <Dialog open={showStopOpsConfirm} onOpenChange={setShowStopOpsConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              🔴 Stop Taking Orders?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-gray-700">
            <p>New online orders will be blocked immediately, and the customer app will show the store as closed.</p>
            <p className="font-medium text-red-700">
              Any order already placed and in progress will stay on hold — it won't be cancelled automatically.
              Please call the affected customer(s) to let them know.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStopOpsConfirm(false)}>Cancel</Button>
            <Button
              className="bg-red-700 hover:bg-red-800"
              onClick={handleStopOperations}
              disabled={stopOpsLoading}
            >
              {stopOpsLoading ? 'Stopping...' : 'Yes, Stop Operations'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Store Hours Modal - weekly schedule applied consistently across
          POS, Assisted Order, and online ordering. */}
      <Dialog open={showStoreHoursModal} onOpenChange={setShowStoreHoursModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🕐 Weekly Store Hours
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">
            Start Day / End Day are the real, unconditional controls for taking orders - always work, any time.
            This schedule acts as a safety net: if the day is ever left running past closing time with nobody
            around to notice, it automatically stops new orders from coming in (orders already in progress are
            never affected).
          </p>
          <div className="space-y-2 py-2">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
              <div key={day} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200">
                <span className="w-20 text-sm font-medium capitalize shrink-0">{day}</span>
                <Checkbox
                  id={`closed-${day}`}
                  checked={storeHours[day].closed}
                  onCheckedChange={(checked) => setStoreHours({ ...storeHours, [day]: { ...storeHours[day], closed: checked } })}
                />
                <Label htmlFor={`closed-${day}`} className="text-xs text-gray-500 shrink-0">Closed</Label>
                <Input
                  type="time"
                  value={storeHours[day].open}
                  disabled={storeHours[day].closed}
                  onChange={(e) => setStoreHours({ ...storeHours, [day]: { ...storeHours[day], open: e.target.value } })}
                  className="flex-1 h-8 text-xs"
                />
                <span className="text-gray-400 text-xs">to</span>
                <Input
                  type="time"
                  value={storeHours[day].close}
                  disabled={storeHours[day].closed}
                  onChange={(e) => setStoreHours({ ...storeHours, [day]: { ...storeHours[day], close: e.target.value } })}
                  className="flex-1 h-8 text-xs"
                />
              </div>
            ))}
          </div>

          <div className="border-t pt-3 mt-1">
            <p className="text-sm font-semibold mb-1">Scheduled Leaves</p>
            <p className="text-xs text-gray-500 mb-2">
              One-off closed dates (holidays, personal days) - separate from the weekly pattern above.
              Start Day always overrides this too if you need to open anyway.
            </p>
            <div className="flex gap-2 mb-2">
              <Input
                type="date"
                value={newLeaveDate}
                onChange={(e) => setNewLeaveDate(e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <Input
                placeholder="Reason (optional)"
                value={newLeaveReason}
                onChange={(e) => setNewLeaveReason(e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 shrink-0"
                onClick={() => {
                  if (!newLeaveDate) { toast.error('Pick a date first'); return; }
                  setScheduledLeaves([...scheduledLeaves, { date: newLeaveDate, reason: newLeaveReason || 'Scheduled leave' }]);
                  setNewLeaveDate('');
                  setNewLeaveReason('');
                }}
              >
                Add
              </Button>
            </div>
            {scheduledLeaves.length > 0 && (
              <div className="space-y-1">
                {scheduledLeaves.map((leave, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                    <span>{leave.date} — {leave.reason}</span>
                    <button
                      onClick={() => setScheduledLeaves(scheduledLeaves.filter((_, i) => i !== idx))}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreHoursModal(false)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSaveStoreHours}
              disabled={storeHoursLoading}
            >
              {storeHoursLoading ? 'Saving...' : 'Save Store Hours'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orders-still-in-progress prompt at scheduled close time - the
          real safety net. If nobody answers within a few minutes, the
          backend safely defaults to closing on its own. */}
      <Dialog open={showClosePrompt} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>⏰ Past closing time</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Orders are still in progress. Do you wish to continue the day?
          </p>
          <p className="text-xs text-gray-400">
            If you don't respond within a few minutes, new orders will stop automatically (orders already in progress won't be affected).
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleClosePromptResponse('no')}
              disabled={closePromptLoading}
            >
              No, stop new orders
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleClosePromptResponse('yes')}
              disabled={closePromptLoading}
            >
              Yes, continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStoreLocationModal} onOpenChange={setShowStoreLocationModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" /> Store Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-gray-500">
              Set this store's exact coordinates once. Every customer's delivery
              distance and radius/fee rules will then calculate automatically —
              no per-order setup needed.
            </p>
            <div>
              <Label>Latitude</Label>
              <Input
                type="number"
                step="any"
                placeholder="e.g. 19.2403"
                value={storeLat}
                onChange={(e) => setStoreLat(e.target.value)}
              />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input
                type="number"
                step="any"
                placeholder="e.g. 73.1305"
                value={storeLng}
                onChange={(e) => setStoreLng(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-400">
              Tip: open{' '}
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                Google Maps
              </a>
              , right-click the store's exact spot, and the coordinates shown
              at the top are what to paste here.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreLocationModal(false)}>Cancel</Button>
            <Button
              className="bg-indigo-700 hover:bg-indigo-800"
              onClick={handleSaveStoreLocation}
              disabled={storeLocationLoading}
            >
              {storeLocationLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Notifications */}
      <Dialog open={showOrderNotificationsModal} onOpenChange={setShowOrderNotificationsModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Order Notifications
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!pushSupported ? (
              <p className="text-sm text-gray-500">
                This device or browser doesn't support push notifications.
                Try a recent version of Chrome on Android, or Safari on iPhone
                (after adding this app to your Home Screen).
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500">
                  Get a real phone notification the moment a new online order
                  comes in — even with your phone locked or this app closed.
                  Uses your phone's own notification sound, not a fixed one.
                </p>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">
                      {pushSubscribed ? 'Enabled on this device' : 'Not enabled on this device'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {pushSubscribed ? "You'll be notified here" : 'Turn on to get notified here'}
                    </p>
                  </div>
                  <Switch
                    checked={pushSubscribed}
                    disabled={pushLoading}
                    onCheckedChange={(checked) => {
                      if (checked) handleEnablePushNotifications();
                      else handleDisablePushNotifications();
                    }}
                  />
                </div>
                <p className="text-xs text-gray-400">
                  This is per-device — each staff member or manager who wants
                  alerts needs to turn this on from their own phone once.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderNotificationsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offers Manager */}
      <OffersManager 
        storeId={user?.store_id} 
        isOpen={showOffersManager} 
        onClose={() => setShowOffersManager(false)} 
      />

      {/* Customer History Lookup */}
      <CustomerHistoryLookup
        storeId={user?.store_id}
        isOpen={showCustomerLookup}
        onClose={() => setShowCustomerLookup(false)}
        onSelectCustomer={(customer) => {
          setShowCustomerLookup(false);
          navigate('/pos/assisted-order', { state: { prefillCustomer: { name: customer.name, phone: customer.phone } } });
        }}
      />
    </div>
  );
};

export default POSDashboard;
